import { z } from 'zod';

export const growthGoalStateSchema = z.enum(['draft', 'open', 'waiting-for-agreement', 'in-review', 'done']);
export type GrowthGoalState = z.infer<typeof growthGoalStateSchema>;

export const growthGoalVisibilitySchema = z.enum(['private', 'shared']);
export type GrowthGoalVisibility = z.infer<typeof growthGoalVisibilitySchema>;

export const growthCheckpointMarkSchema = z.enum(['on-track', 'needs-support', 'changed']);
export type GrowthCheckpointMark = z.infer<typeof growthCheckpointMarkSchema>;

export const growthOutcomeResultSchema = z.enum(['reached', 'partly', 'dropped']);
export type GrowthOutcomeResult = z.infer<typeof growthOutcomeResultSchema>;

export const growthMeasureSchema = z.discriminatedUnion('type', [
    z.object({
        current: z.number(),
        start: z.number(),
        target: z.number(),
        type: z.literal('number'),
        unit: z.string(),
    }),
    z.object({ current: z.number(), type: z.literal('percent') }),
    z.object({ isDone: z.boolean(), type: z.literal('done') }),
    z.object({
        milestones: z.array(z.object({ due: z.iso.date(), id: z.string(), isDone: z.boolean(), title: z.string() })),
        type: z.literal('milestones'),
    }),
]);
export type GrowthMeasure = z.infer<typeof growthMeasureSchema>;
export type GrowthMeasureType = GrowthMeasure['type'];

export const growthCheckpointSchema = z.object({
    doneAt: z.iso.date().nullish(),
    due: z.iso.date(),
    id: z.string(),
    mark: growthCheckpointMarkSchema.nullish(),
    note: z.string().nullish(),
    value: z.number().nullish(),
});
export type GrowthCheckpoint = z.infer<typeof growthCheckpointSchema>;

export const growthCommentSchema = z.object({
    authorName: z.string(),
    authorRole: z.enum(['owner', 'manager']),
    createdAt: z.iso.datetime(),
    id: z.string(),
    text: z.string(),
});
export type GrowthComment = z.infer<typeof growthCommentSchema>;

export const growthOutcomeSchema = z.object({
    confirmedAt: z.iso.date().nullish(),
    note: z.string(),
    result: growthOutcomeResultSchema,
});
export type GrowthOutcome = z.infer<typeof growthOutcomeSchema>;

export const growthCompanyGoalSchema = z.object({
    description: z.string(),
    id: z.string(),
    sortOrder: z.number().int().nullish(),
    title: z.string(),
});
export type GrowthCompanyGoal = z.infer<typeof growthCompanyGoalSchema>;

export const growthPersonPositionSchema = z.object({
    id: z.string(),
    title: z.string(),
});
export type GrowthPersonPosition = z.infer<typeof growthPersonPositionSchema>;

export const growthPersonSchema = z.object({
    id: z.string(),
    level: z.string().nullish(),
    name: z.string(),
    positions: z.array(growthPersonPositionSchema).default([]),
    positionTitle: z.string(),
});
export type GrowthPerson = z.infer<typeof growthPersonSchema>;

export const growthLearningProposalSchema = z.object({
    duration: z.string(),
    title: z.string(),
});

export const growthGoalSchema = z.object({
    checkpoints: z.array(growthCheckpointSchema),
    comments: z.array(growthCommentSchema),
    createdAt: z.iso.date(),
    description: z.string(),
    due: z.iso.date().nullish(),
    id: z.string(),
    isStale: z.boolean(),
    learningProposal: growthLearningProposalSchema.nullish(),
    measure: growthMeasureSchema.nullish(),
    outcome: growthOutcomeSchema.nullish(),
    ownerId: z.string(),
    paysIntoId: z.string().nullish(),
    returnComment: z.string().nullish(),
    setBy: z.enum(['person', 'manager']),
    state: growthGoalStateSchema,
    title: z.string(),
    visibility: growthGoalVisibilitySchema,
});
export type GrowthGoal = z.infer<typeof growthGoalSchema>;

export const growthChangeNoticeSchema = z.object({
    at: z.iso.date(),
    id: z.string(),
    kind: z.enum(['position', 'manager', 'tasks']),
    message: z.string(),
});
export type GrowthChangeNotice = z.infer<typeof growthChangeNoticeSchema>;

export const growthMyGoalsSchema = z.object({
    changeNotices: z.array(growthChangeNoticeSchema),
    isAvailable: z.boolean().default(true),
    unavailableReason: z.enum(['no-manager']).nullish(),
    companyGoals: z.array(growthCompanyGoalSchema),
    goals: z.array(growthGoalSchema),
    hasTeam: z.boolean(),
    person: growthPersonSchema,
});
export type GrowthMyGoals = z.infer<typeof growthMyGoalsSchema>;

export const growthWaitingItemSchema = z.object({
    goalId: z.string(),
    goalTitle: z.string(),
    kind: z.enum(['agreement', 'outcome', 'needs-support']),
    person: growthPersonSchema,
});
export type GrowthWaitingItem = z.infer<typeof growthWaitingItemSchema>;

export const growthTeamMemberSchema = z.object({
    goals: z.array(growthGoalSchema),
    person: growthPersonSchema,
});
export type GrowthTeamMember = z.infer<typeof growthTeamMemberSchema>;

export const growthTeamSchema = z.object({
    members: z.array(growthTeamMemberSchema),
    waiting: z.array(growthWaitingItemSchema),
});
export type GrowthTeam = z.infer<typeof growthTeamSchema>;

export const growthGoalDetailSchema = z.object({
    companyGoals: z.array(growthCompanyGoalSchema),
    goal: growthGoalSchema,
    owner: growthPersonSchema,
    viewerRole: z.enum(['owner', 'manager', 'viewer']),
});
export type GrowthGoalDetail = z.infer<typeof growthGoalDetailSchema>;

export const growthCreateGoalRequestSchema = z.object({
    description: z.string(),
    due: z.iso.date().nullish(),
    measure: growthMeasureSchema.nullish(),
    paysIntoId: z.string().nullish(),
    share: z.boolean(),
    suggestionId: z.string().nullish(),
    title: z.string().min(1),
});
export type GrowthCreateGoalRequest = z.infer<typeof growthCreateGoalRequestSchema>;

export const growthGoalActionRequestSchema = z.discriminatedUnion('action', [
    z.object({ action: z.literal('share') }),
    z.object({ action: z.literal('keep-private') }),
    z.object({ action: z.literal('agree') }),
    z.object({ action: z.literal('send-back'), comment: z.string().min(1) }),
    z.object({ action: z.literal('mark-done') }),
    z.object({ action: z.literal('propose-outcome'), note: z.string(), result: growthOutcomeResultSchema }),
    z.object({ action: z.literal('confirm-outcome') }),
    z.object({ action: z.literal('extend'), due: z.iso.date() }),
    z.object({ action: z.literal('comment'), text: z.string().min(1) }),
    z.object({ action: z.literal('set-pays-into'), paysIntoId: z.string().nullable() }),
    z.object({ action: z.literal('archive') }),
    z.object({
        action: z.literal('update-checkpoint'),
        checkpointId: z.string().nullish(),
        mark: growthCheckpointMarkSchema,
        note: z.string(),
        value: z.number().nullish(),
    }),
]);
export type GrowthGoalActionRequest = z.infer<typeof growthGoalActionRequestSchema>;

export const growthCompanyGoalUpsertSchema = z.object({
    description: z.string(),
    title: z.string().min(1),
});
export type GrowthCompanyGoalUpsert = z.infer<typeof growthCompanyGoalUpsertSchema>;

export const growthCompanyGoalsSchema = z.object({
    companyGoals: z.array(growthCompanyGoalSchema),
});

export const growthOrganizationTeamSchema = z.object({
    id: z.string(),
    manager: growthPersonSchema,
    members: z.array(growthTeamMemberSchema),
    name: z.string(),
});
export type GrowthOrganizationTeam = z.infer<typeof growthOrganizationTeamSchema>;

export const growthOrganizationSchema = z.object({
    page: z.number().int().nullish(),
    size: z.number().int().nullish(),
    teams: z.array(growthOrganizationTeamSchema),
    totalTeams: z.number().int().nullish(),
});
export type GrowthOrganization = z.infer<typeof growthOrganizationSchema>;

export const growthRecommendationSchema = z.object({
    description: z.string(),
    id: z.string(),
    measure: growthMeasureSchema.nullish(),
    paysIntoId: z.string().nullish(),
    title: z.string(),
    tracking: z.string(),
});
export type GrowthRecommendation = z.infer<typeof growthRecommendationSchema>;

export const growthRecommendationsStatusSchema = z.enum(['pending', 'ready', 'failed']);
export type GrowthRecommendationsStatus = z.infer<typeof growthRecommendationsStatusSchema>;

export const growthRecommendationsSchema = z.object({
    basis: z.string(),
    recommendations: z.array(growthRecommendationSchema),
    status: growthRecommendationsStatusSchema.default('ready'),
});
export type GrowthRecommendations = z.infer<typeof growthRecommendationsSchema>;
